import cn from 'classnames'
import { FC, useCallback } from 'react'

import { NumberInput } from 'cng-common/components/numberInput'
import { useSafetyTranslation } from 'cng-common/features/translations'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Nullable } from 'cng-common/models/utils'
import { Tooltip } from 'core-components/library/tooltip'

import { useFormField } from '../../hooks/useFormField'
import { ReadOnlyField } from '../readOnlyField'

import { IFormNumberInput } from './models'

import styles from './FormNumberInput.module.scss'

export const FormNumberInput: FC<IFormNumberInput> = ({
  field,
  formKey,
  label = '',
  defaultValue,
  format,
  className,
  isDisabled,
  isRequired,
  isReadOnly,
  isVisible = true,
  onChange,
  onBlur,
  onValidate,
  validationType,
  max,
  min,
  placeholder,
}) => {
  const { handleBlur, handleChange, value, validationMessage, isModified } = useFormField<
    Nullable<number>
  >({
    field,
    formKey,
    defaultValue,
    onChange,
    onValidate,
    isDisabled,
    isRequired,
    isReadOnly,
    isVisible,
    onBlur,
    validationType,
  })

  const t = useSafetyTranslation()
  const translatedPlaceholder = placeholder ? t(placeholder) : undefined

  const processingChangeEvent = useCallback(
    async (newValue: string) => {
      if (newValue === '') {
        await handleChange(null)
        return
      }

      const parsedToNumber = Number(newValue)
      await handleChange(isNaN(parsedToNumber) ? null : parsedToNumber)
    },
    [handleChange]
  )

  if (isVisible) {
    if (isReadOnly) {
      return <ReadOnlyField label={label} value={value} />
    }

    return (
      <div className={styles.validationContainer}>
        <Tooltip disabled={!validationMessage} tipContent={() => <>{validationMessage}</>}>
          <NumberInput
            disabled={isDisabled}
            classes={{
              inputClasses: {
                root: cn(className, {
                  [styles.required]: isRequired && Boolean(label),
                  [styles.modified]: isModified,
                  [styles.invalid]: validationMessage,
                }),
              },
            }}
            label={label && <Translation text={label} span />}
            value={value?.toString()}
            onChange={processingChangeEvent}
            onBlur={handleBlur}
            format={format}
            max={max}
            min={min}
            placeholder={translatedPlaceholder}
          />
        </Tooltip>
      </div>
    )
  }

  return null
}
